import { Close } from "@mui/icons-material";
import { Box, css, IconButton, styled, Typography } from "@mui/material";
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { TOOLBAR_COLORS } from "components/toolbars/constants";
import { useDeviceOrientation } from "contexts/panelMode";
import { PanelPickerOverlayContext } from "contexts/panelPickerOverlay";

import { DrawerFabAnchorContext, type RegisterDrawerFabAnchor } from "../drawerFabAnchorContext";
import { DrawerPeekTargetContext, type RegisterDrawerPeekTarget } from "../drawerPeekTargetContext";
import { WindowMobileTargetContext } from "../windowMobileTargetContext";
import { DRAWER_ANIMATION_DURATION, DRAWER_COLORS, DRAWER_LAYOUT, DRAWER_WIDTH, DRAWER_Z_INDEX } from "./constants";
import { findContentScrollRegion, findScrollableAncestor } from "./scrollRegion";
import {
    collapseBoundaryVh,
    COLLAPSED_HEIGHT_FALLBACK,
    collapsedPeekVh,
    fabShiftBetween,
    findNearestSnap,
    inferredContentHeader,
    landscapeSnapHeight,
    nearestLandscapeSnap,
    peekHeight,
    snapDown,
    snapHeight,
    type SnapPoint,
    snapUp,
} from "./snapPoints";

// Extra distance (px) past the FAB's measured off-screen point, so the drop shadow and any
// safe-area drift are carried out of view with it.
const FAB_HIDE_MARGIN = 24;

// Fast-swipe threshold in px/ms — above this, snap to next point in swipe direction
const VELOCITY_THRESHOLD = 0.5;

// Travel (px) before an undecided gesture is classified as a drag or a content scroll.
const AXIS_THRESHOLD = 6;

/** The touch point with `identifier` in `list`, or null when that finger is not in it. */
function findTouch(list: TouchList, identifier: number): Touch | null {
    for (let i = 0; i < list.length; i++) {
        if (list[i].identifier === identifier) {
            return list[i];
        }
    }
    return null;
}

// Single container for both orientations — keeping it one component type means
// React never unmounts children on rotation, preserving drawer content state.
const DrawerContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPortrait",
})<{ isPortrait: boolean }>(
    ({ isPortrait }) => css`
        position: fixed;
        bottom: 0;
        background-color: ${DRAWER_COLORS.BG};
        border-radius: 16px 16px 0 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        z-index: ${DRAWER_Z_INDEX};
        pointer-events: auto;
        /* Keeps content above the iOS home indicator at every snap, in both orientations.
           Border-box so the sheet's height is its full extent, padding included — the
           collapsed peek is measured to that same number (see measurePeek), which keeps
           the rest position, the drag floor and the FAB math in one unit. */
        box-sizing: border-box;
        padding-bottom: env(safe-area-inset-bottom, 0px);
        /* Height is set inline from the current snap point in both orientations — see the
           snap-sync useLayoutEffect in DrawerComponent. Deliberately no class rule here to
           avoid a misleading fallback. Landscape is bottom-anchored too, rather than
           top+bottom: collapsing by height keeps the safe-area padding inside the visible
           sheet, where a translateY would carry it off-screen and show body under the peek. */

        ${isPortrait
            ? css`
                  left: 0;
                  right: 0;
                  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
                  overscroll-behavior: none;
              `
            : css`
                  right: ${DRAWER_LAYOUT.RIGHT_MARGIN}px;
                  width: ${DRAWER_WIDTH}px;
                  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
              `}
    `,
);

// Handle + header. Never shrinks: the collapsed peek is sized to this, so squashing it
// would clip the chrome the peek exists to keep on screen.
const DragZone = styled(Box)`
    flex-shrink: 0;
`;

const DragHandleArea = styled(Box)`
    min-height: ${DRAWER_LAYOUT.DRAG_HANDLE_HEIGHT}px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
`;

const DragHandle = styled(Box)`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: ${DRAWER_COLORS.BORDER};
    flex-shrink: 0;
    position: relative;
    z-index: 11;
`;

const DrawerHeader = styled(Box)(
    () => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background-color: ${DRAWER_COLORS.HEADER_BG};
        min-height: ${DRAWER_LAYOUT.HEADER_MIN_HEIGHT}px;
        flex-shrink: 0;
    `,
);

const HeaderLeft = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${DRAWER_COLORS.TEXT};
`;

const HeaderIcon = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 20px;
        height: 20px;
        color: ${DRAWER_COLORS.ICON};
    }
`;

const CloseButton = styled(IconButton)`
    padding: 8px;
    background-color: ${DRAWER_COLORS.BUTTON_BG};
    border-radius: 8px;
    color: ${DRAWER_COLORS.ICON};

    &:hover {
        background-color: ${TOOLBAR_COLORS.HOVER_DARK};
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const DrawerBody = styled(Box)(
    () => css`
        flex: 1;
        min-height: 0;
        overflow: auto;
        background-color: ${DRAWER_COLORS.BG};
        -webkit-overflow-scrolling: touch;
    `,
);

export interface DrawerComponentProps {
    /** Drawer title */
    title: string;
    /** Optional subtitle shown below the title (e.g. selected object name) */
    subtitle?: string;
    /** Optional icon */
    icon?: ReactNode;
    /** Drawer content */
    children: ReactNode;
    /** Whether drawer is open */
    isOpen: boolean;
    /** Close handler */
    onClose: () => void;
    /**
     * When true, the drag handle and internal header (title, close button) are not rendered.
     * Use when the drawer content supplies its own header. The collapsed peek is inferred
     * from the content's scroll region, or sized exactly if that header registers its title
     * row via `useDrawerPeekTarget`. The visible peek (handle plus that header) is the
     * drag surface — not just the 44px handle.
     */
    hideHeader?: boolean;
    /** Optional test ID */
    "data-testid"?: string;
}

/**
 * Drawer Component
 *
 * A panel that slides in from the edge of the screen for phone UI.
 * - Portrait: slides up from bottom, full width. Supports snap points at 50%, 75%, 100%,
 *   plus a collapsed peek sized to the chrome. Slow drag snaps to the nearest of those;
 *   fast swipe steps one snap point in the swipe direction. Dragging down bottoms out at
 *   the peek — the close button is the only way to dismiss, so a collapsed drawer (which
 *   keeps its content mounted) always leaves a visible cue that it is still active. The
 *   drag surface is the whole sheet: the chrome always drags, content drags only on a
 *   downward pull from the top of its scroll region, and a collapsed drawer drags
 *   anywhere — its visible sliver is header, not scrollable content.
 * - Landscape: a 375px panel at the right, bottom-anchored, reaching up to the top bar when
 *   open. Two resting places — fully open and the same collapsed peek — and it collapses
 *   the same way the sheet does, by height, so only its header remains at the bottom edge.
 *   Same drag surface and dismissal rules as portrait.
 *
 * Both orientations run one gesture model in "visible px" — the sheet's height, which is
 * how much of it is on screen. Only the snap ladder and the open height differ.
 */
export function DrawerComponent({
    title,
    subtitle,
    icon,
    children,
    isOpen,
    onClose,
    hideHeader = false,
    "data-testid": dataTestId,
}: DrawerComponentProps) {
    const orientation = useDeviceOrientation();
    const isPortrait = orientation === "portrait";

    const [pickerOverlay, setPickerOverlay] = useState<ReactNode>(null);
    const overlayContextValue = useMemo(() => ({ setOverlay: setPickerOverlay }), []);

    // Snap point is authoritative on the sheet's resting position — its height in portrait,
    // its translation in landscape (see the snap-sync useLayoutEffect below). State drives
    // the effect; the ref mirror lets the stable touch-handler closure read the current
    // value synchronously. Call sites update both.
    const [snapPoint, setSnapPoint] = useState<SnapPoint>(50);
    const snapPointRef = useRef<SnapPoint>(50);

    // Height of the collapsed peek in px. Measured rather than fixed because the chrome it
    // has to keep on screen varies: `hideHeader`, the optional subtitle, and — for the
    // content-owned header most drawers use — whatever that panel renders as its chrome.
    const [collapsedHeightPx, setCollapsedHeightPx] = useState(COLLAPSED_HEIGHT_FALLBACK);
    const collapsedHeightRef = useRef(COLLAPSED_HEIGHT_FALLBACK);

    const containerRef = useRef<HTMLDivElement>(null);
    const dragZoneRef = useRef<HTMLDivElement>(null); // drag handle + the drawer's own header
    const bodyRef = useRef<HTMLDivElement>(null);
    // Title row registered by the content through DrawerPeekTargetContext, plus the
    // observer watching it. Null when the content owns its header but registers nothing.
    const peekTargetRef = useRef<HTMLElement | null>(null);
    const peekObserverRef = useRef<ResizeObserver | null>(null);
    // Content scroll region currently observed, and the tallest header height inferred from
    // its offset. The cache carries that reading through the moments it cannot be retaken,
    // so the peek holds its size instead of snapping to the fallback.
    const scrollRegionRef = useRef<HTMLElement | null>(null);
    const inferredHeaderRef = useRef(0);
    // The registered row's own `flex-shrink` before registration pinned it to 0 (see
    // registerPeekTarget), restored when the row is replaced.
    const peekTargetFlexShrinkRef = useRef("");
    // Set for the duration of a drag. The peek is that drag's floor, so it must not be
    // recomputed underneath the user's finger — and re-measuring per touchmove would read
    // layout on every frame.
    const isDraggingRef = useRef(false);
    // True for the snap-height transition after release. Observer callbacks during that
    // window are dropped (they would force layout every animation frame) and flushed once
    // by `measurePeek` when the transition ends.
    const isSnapAnimatingRef = useRef(false);
    const measureRafRef = useRef(0);
    // FAB registered through DrawerFabAnchorContext, plus how far down (px) it must travel
    // to be fully off-screen — measured at registration, while its transform is still home.
    const fabRef = useRef<HTMLElement | null>(null);
    const fabHideDistanceRef = useRef(0);
    // Identifier of the finger driving the current gesture. Other fingers are ignored for
    // its duration, and only this one lifting ends it.
    const gestureTouchId = useRef<number | null>(null);
    const dragStartX = useRef<number | null>(null);
    const dragStartY = useRef<number | null>(null);
    // Visible sheet height (px) captured when the drag begins. Drag math uses this as the
    // baseline (rather than snapPointRef.current) so a drag that begins mid snap-transition
    // continues from what is on screen instead of the last committed target.
    const dragStartVisiblePx = useRef(0);
    const dragOffset = useRef(0);
    const gestureMode = useRef<"undecided" | "dragging" | "scrolling">("undecided");
    const scrollableAncestor = useRef<HTMLElement | null>(null);
    const lastTouchY = useRef(0);
    const lastTouchTime = useRef(0);
    // Read through a ref so `measurePeek` — and the registration callback and touch
    // handlers that close over it — stay stable for the lifetime of the component.
    const hideHeaderRef = useRef(hideHeader);
    hideHeaderRef.current = hideHeader;
    const isPortraitRef = useRef(isPortrait);
    isPortraitRef.current = isPortrait;
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;
    // Whether the previous render was open. Written by the open/close effect (passive), read
    // by it and by the orientation-flip layout effect, so both can tell a fresh open (reset
    // the snap, play the entry animation) from a flip while open (carry the snap across,
    // animate nothing).
    const wasOpenRef = useRef(false);

    // The content's header height, from the better of two sources.
    //
    // An explicitly registered row wins: the content named the element, so there is nothing
    // to infer. Otherwise infer it from where the content's scroll region starts, which
    // needs no cooperation and covers the panels that render their own chrome. Re-resolves
    // the observed scroll region as it does so, since a view switch replaces that element.
    const resolveContentHeader = useCallback(() => {
        // Drawers that render their own chrome must not pick up a content toolbar sitting
        // above a scroll region — that would pad the peek with chrome the user already has.
        if (!hideHeaderRef.current) {
            return 0;
        }

        // Trusted as-is: registration pins the row's `flex-shrink` to 0, so unlike an
        // inferred header it cannot read compressed, and a row that genuinely gets shorter
        // (a subtitle clearing) brings the peek down with it.
        const registered = peekTargetRef.current?.offsetHeight ?? 0;
        if (registered > 0) {
            return registered;
        }

        const body = bodyRef.current;
        if (!body) {
            return inferredHeaderRef.current;
        }

        const scrollRegion = findContentScrollRegion(body);
        if (scrollRegion && scrollRegion !== scrollRegionRef.current) {
            // A view switch replaces the scroll region, and the header above it with it, so
            // the previous content's reading is discarded rather than carried over.
            const previous = scrollRegionRef.current;
            if (previous) {
                peekObserverRef.current?.unobserve(previous);
            }
            scrollRegionRef.current = scrollRegion;
            inferredHeaderRef.current = 0;
            peekObserverRef.current?.observe(scrollRegion);
        }
        if (!scrollRegion) {
            // Momentarily absent during a content re-render. Holding the last reading
            // avoids a visible drop to the fallback and back.
            return inferredHeaderRef.current;
        }

        const offset = scrollRegion.getBoundingClientRect().top - body.getBoundingClientRect().top;
        inferredHeaderRef.current = inferredContentHeader(offset, inferredHeaderRef.current);
        return inferredHeaderRef.current;
    }, []);

    // Re-measure the peek: drawer chrome plus the content's header. Layout reports 0 before
    // first paint and in non-layout environments (jsdom), where the fallback stands. Writes
    // the ref as well as state so the stable touch-handler closure can read the fresh value
    // synchronously.
    const measurePeek = useCallback(() => {
        if (isDraggingRef.current || isSnapAnimatingRef.current) {
            return;
        }
        const dragZone = dragZoneRef.current;
        if (!dragZone) {
            return;
        }
        // The container carries the safe-area inset as bottom padding, and the peek is the
        // full inline height the sheet rests at — so it includes that padding. One number
        // then serves the rest height, the touchmove floor, the snap boundary and the FAB.
        const container = containerRef.current;
        const safeAreaPx = container ? parseFloat(getComputedStyle(container).paddingBottom) || 0 : 0;
        const measured = peekHeight(dragZone.offsetHeight, resolveContentHeader(), !hideHeaderRef.current) + safeAreaPx;
        if (measured > 0 && measured !== collapsedHeightRef.current) {
            collapsedHeightRef.current = measured;
            setCollapsedHeightPx(measured);
        }
    }, [resolveContentHeader]);

    // Coalesces ResizeObserver callbacks to one layout read per frame. The observer fires
    // on every tick of the snap-height transition otherwise.
    const scheduleMeasurePeek = useCallback(() => {
        if (measureRafRef.current !== 0) {
            return;
        }
        measureRafRef.current = requestAnimationFrame(() => {
            measureRafRef.current = 0;
            measurePeek();
        });
    }, [measurePeek]);

    /**
     * The most of the sheet that can be on screen (px): the viewport in portrait, everything
     * below the top bar in landscape. Everything in "visible px" is clamped to it.
     */
    const maxVisiblePx = useCallback(
        () => (isPortraitRef.current ? window.innerHeight : window.innerHeight - DRAWER_LAYOUT.TOP_BAR_HEIGHT),
        [],
    );

    /** Inline `height` a snap resolves to in the current orientation. */
    const restHeight = useCallback(
        (snap: SnapPoint) =>
            isPortraitRef.current
                ? snapHeight(snap, collapsedHeightRef.current)
                : landscapeSnapHeight(snap, collapsedHeightRef.current, DRAWER_LAYOUT.TOP_BAR_HEIGHT),
        [],
    );

    /** Visible sheet height (px) a snap point resolves to; what `applyFabShift` keys on. */
    const snapVisiblePx = useCallback(
        (snap: SnapPoint) => {
            if (snap === "collapsed") {
                return collapsedHeightRef.current;
            }
            // Landscape's only open snap is 100; anything else carried over from portrait
            // means fully open there too.
            return isPortraitRef.current ? (snap / 100) * window.innerHeight : maxVisiblePx();
        },
        [maxVisiblePx],
    );

    // Writes the registered FAB's vertical transform for a drawer showing `visibleHeightPx`
    // of itself. Mirrors how the sheet's own position is driven: per-frame writes during a
    // drag (`animate: false`), a single eased transition on snap commits (`animate: true`).
    // Imperative for the same reason the sheet is — a re-render per touchmove frame is not
    // an option.
    const applyFabShift = useCallback(
        (visibleHeightPx: number, animate: boolean) => {
            const fab = fabRef.current;
            if (!fab) {
                return;
            }
            const peek = collapsedHeightRef.current;
            // Where the FAB starts to ride down: halfway from the peek to the next resting
            // place up — the 50 snap in portrait, fully open in landscape.
            const boundaryPx = isPortraitRef.current
                ? (collapseBoundaryVh(collapsedPeekVh(peek, window.innerHeight)) / 100) * window.innerHeight
                : (peek + maxVisiblePx()) / 2;
            const shift = fabShiftBetween(visibleHeightPx, peek, boundaryPx, fabHideDistanceRef.current);
            fab.style.transition = animate ? `transform ${DRAWER_ANIMATION_DURATION}ms ease-out` : "none";
            fab.style.transform = shift > 0 ? `translateY(${shift}px)` : "";
        },
        [maxVisiblePx],
    );

    // How far down (px) the registered FAB must travel to be fully off-screen: viewport
    // bottom minus its top, while it sits at home. Only meaningful while no transform is
    // applied, so callers must ensure the FAB is home — the guard is a safety net.
    const measureFabHideDistance = useCallback(() => {
        const fab = fabRef.current;
        // An inline transform of "" is not enough on its own: the FAB may still be gliding
        // home from a previous shift, and its rect mid-transition is not its home.
        if (!fab || fab.style.transform !== "" || fab.getAnimations().length > 0) {
            return;
        }
        fabHideDistanceRef.current = window.innerHeight - fab.getBoundingClientRect().top + FAB_HIDE_MARGIN;
    }, []);

    // Stable so it can serve directly as the FAB wrapper's `ref`.
    const registerFabAnchor = useCallback<RegisterDrawerFabAnchor>(
        (node) => {
            if (fabRef.current === node) {
                return;
            }
            fabRef.current = node;
            if (!node) {
                return;
            }
            // The node just mounted at its home position, so this is its full exit travel.
            // Re-measured on every orientation change, since the safe-area inset differs.
            measureFabHideDistance();
            // A FAB can mount while the drawer is already collapsed (a content swap), and
            // must appear where the current snap expects it rather than sliding from home.
            // Not at the drawer's own mount, though: child refs attach before the parent's,
            // so the container is not measurable yet — landscape's "fully open" would read
            // as 0 and shift the FAB off-screen. The snap-sync effect places it then.
            if (!containerRef.current) {
                return;
            }
            applyFabShift(snapVisiblePx(snapPointRef.current), false);
        },
        [applyFabShift, measureFabHideDistance, snapVisiblePx],
    );

    // Stable so it can serve directly as a child's `ref` — a changing identity would make
    // React detach and reattach the node on every render.
    const registerPeekTarget = useCallback<RegisterDrawerPeekTarget>(
        (node) => {
            const previous = peekTargetRef.current;
            if (previous === node) {
                return;
            }
            if (previous) {
                peekObserverRef.current?.unobserve(previous);
                previous.style.flexShrink = peekTargetFlexShrinkRef.current;
            }
            peekTargetRef.current = node;
            if (node) {
                // The row's height *is* the peek, so it must never compress. A flex item
                // without `flex-shrink: 0` does exactly that as the drawer runs out of room,
                // and a reading taken then would shrink the peek — compressing the row
                // further — on every pass. Pinning it here lets the latest reading be
                // trusted instead of ratcheting to the tallest one ever seen, so a row that
                // genuinely gets shorter brings the peek down with it. A no-op on anything
                // that is not a flex item, which could not compress anyway.
                peekTargetFlexShrinkRef.current = node.style.flexShrink;
                node.style.flexShrink = "0";
                peekObserverRef.current?.observe(node);
            }
            measurePeek();
        },
        [measurePeek],
    );

    // A content header can change height without re-rendering this component — a title
    // wrapping to two lines, a subtitle arriving with fetched data. Watching the drawer's
    // own chrome, the registered row, and the content's scroll region (which shrinks as the
    // header above it grows) keeps a collapsed drawer sized to what it has to show, rather
    // than clipping until the next drag re-measures.
    useEffect(() => {
        if (typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(() => scheduleMeasurePeek());
        peekObserverRef.current = observer;
        // Child refs attach and the mount measurement runs before parent effects, so these
        // may already be resolved but unobserved — the observer did not exist yet.
        for (const node of [dragZoneRef.current, peekTargetRef.current, scrollRegionRef.current]) {
            if (node) {
                observer.observe(node);
            }
        }
        return () => {
            observer.disconnect();
            peekObserverRef.current = null;
            scrollRegionRef.current = null;
            if (measureRafRef.current !== 0) {
                cancelAnimationFrame(measureRafRef.current);
                measureRafRef.current = 0;
            }
        };
    }, [scheduleMeasurePeek]);

    // First paint, plus the changes the observer cannot attribute: `hideHeader` and
    // orientation decide which elements exist at all. Content-driven resizes of an already
    // registered row arrive through the ResizeObserver above.
    useLayoutEffect(() => {
        measurePeek();
    }, [measurePeek, isPortrait, hideHeader]);

    // Orientation, before paint. Two jobs:
    // - A fresh mount or a closed drawer entering portrait starts off-screen, so the open
    //   animation has somewhere to slide in from. Running in a layout effect guarantees
    //   that is committed before the open/close effect below flushes layout and animates.
    // - A flip while open carries the snap across without replaying anything. "collapsed"
    //   survives in both directions; an open portrait snap becomes landscape's only open
    //   snap, and landscape's becomes portrait's default 50. The transform is pinned home
    //   with no transition, so nothing the previous orientation left behind can animate.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        // The passive open/close effect updates wasOpenRef after this runs, so here it still
        // holds the previous render's value — exactly "was it open before this flip?".
        const openAcrossFlip = isOpenRef.current && wasOpenRef.current;
        el.style.transition = "none";
        if (!openAcrossFlip) {
            el.style.transform = "translateY(100%)";
            return;
        }
        const current = snapPointRef.current;
        const next: SnapPoint = current === "collapsed" ? "collapsed" : isPortrait ? 50 : 100;
        if (next !== current) {
            snapPointRef.current = next;
            setSnapPoint(next);
        }
        el.style.transform = "translateY(0)";
    }, [isPortrait]);

    // Orientation change: re-measure the FAB's exit travel against the new viewport, since
    // the safe-area inset differs between orientations. Declared before the snap-sync
    // effect below so the shift it applies uses the fresh distance. Only takes effect if
    // the FAB is home — a drawer that stays collapsed across the flip keeps its previous
    // reading, which FAB_HIDE_MARGIN absorbs.
    useLayoutEffect(() => {
        measureFabHideDistance();
    }, [isPortrait, measureFabHideDistance]);

    // Keep the sheet's inline height in sync with the current snap point, in both
    // orientations. The snap height bounds DrawerBody's scroll viewport (flex: 1) to the
    // visible portion of the drawer at every snap; any inline height set by touchmove is
    // overwritten here on the next state commit. Height only — the transform belongs to the
    // open/close and flip effects, so nothing here can un-hide a closed drawer or animate
    // across a rotation. `collapsedHeightPx` is a dependency so a peek re-measure re-runs
    // this; `restHeight` reads the matching ref.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        el.style.height = restHeight(snapPoint);
        // Snap commits and peek re-measures move the FAB's resting place with them —
        // e.g. a collapsed drawer whose peek grows pushes the hidden FAB further out.
        applyFabShift(snapVisiblePx(snapPoint), true);
    }, [snapPoint, collapsedHeightPx, isPortrait, applyFabShift, restHeight, snapVisiblePx]);

    // Open and close. A fresh open resets the snap and slides the sheet in; closing slides
    // it out. An orientation flip while open is the flip effect's business — the snap
    // carries across and nothing here runs for it. Always uses explicit inline transforms
    // so stale transforms from touch handlers can never block a subsequent open —
    // important now that DrawerComponent stays mounted.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }

        const justOpened = isOpen && !wasOpenRef.current;
        wasOpenRef.current = isOpen;

        if (!isOpen) {
            el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;
            el.style.transform = "translateY(100%)";
            return;
        }
        if (!justOpened) {
            return;
        }

        // Reset to the default snap on each open so snap state doesn't leak between
        // sessions: portrait's 50, or landscape's only open snap. The snap-sync effect
        // picks this up; every path that mutates the inline position (touchmove,
        // snap-commit) also resyncs it against snapPointRef, so state and inline never
        // disagree here.
        const openSnap: SnapPoint = isPortrait ? 50 : 100;
        snapPointRef.current = openSnap;
        setSnapPoint(openSnap);
        // Flush layout so the flip effect's translateY(100%) is committed before the
        // transition starts — prevents the browser from skipping the animation.
        void el.getBoundingClientRect();
        // Transform-only transition: the snap-sync effect writes the height inline without
        // a height transition, so the drawer doesn't visibly resize as it slides in.
        el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;
        el.style.transform = "translateY(0)";
    }, [isOpen, isPortrait]);

    // Snap-point drag handling on the whole container, both orientations.
    //
    // Deliberately not on the drag zone alone. Every production drawer renders with
    // `hideHeader`, so the drag zone is a bare 44px handle while the chrome the collapsed
    // peek actually shows — the content's own title row — lives in `DrawerBody`. Binding
    // the handle only would leave the thing that looks grabbable inert. The container
    // instead uses a scroll-vs-drag split, so content scrolling still belongs to the content.
    //
    // The gesture is computed in "visible px" — the sheet's height — in both orientations.
    // Only the snap ladder and the resting heights differ.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        let snapSettleTimer = 0;

        const readVisiblePx = () => parseFloat(getComputedStyle(el).height);
        const writeVisiblePx = (px: number) => {
            el.style.height = `${px}px`;
        };
        // Commit a snap with the eased transition. Written explicitly rather than left to
        // the snap-sync effect so the transition still runs when the snap did not change
        // (setSnapPoint is then a no-op and the effect would not fire). Height plus
        // transform (a no-op here) so any pending transform commits cleanly.
        const commitSnap = (snap: SnapPoint) => {
            el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out, height ${DRAWER_ANIMATION_DURATION}ms ease-out`;
            el.style.height = restHeight(snap);
        };
        const nextSnapFor = (visiblePx: number, velocity: number): SnapPoint => {
            if (isPortrait) {
                if (velocity > VELOCITY_THRESHOLD) {
                    return snapDown(snapPointRef.current);
                }
                if (velocity < -VELOCITY_THRESHOLD) {
                    return snapUp(snapPointRef.current);
                }
                const effectiveVh = (visiblePx / window.innerHeight) * 100;
                return findNearestSnap(effectiveVh, collapsedPeekVh(collapsedHeightRef.current, window.innerHeight));
            }
            if (velocity > VELOCITY_THRESHOLD) {
                return "collapsed";
            }
            if (velocity < -VELOCITY_THRESHOLD) {
                return 100;
            }
            return nearestLandscapeSnap(visiblePx, collapsedHeightRef.current, maxVisiblePx());
        };

        // Freeze any in-flight snap transition and capture the current visible height as
        // the drag baseline. Without this, a drag that starts mid-transition would jump
        // because touchmove would recompute from the last committed snap target rather
        // than what the user actually sees on screen.
        const beginDrag = () => {
            // Freezes the peek for the duration of the drag — it is this drag's floor.
            isDraggingRef.current = true;
            isSnapAnimatingRef.current = false;
            window.clearTimeout(snapSettleTimer);

            // Order matters. The computed value is only the interpolated, on-screen one
            // while the transition is still running; clearing the transition first would
            // cancel it and the read would return the snap target instead. So: read, pin
            // that value inline, and only then switch the transition off.
            const visiblePx = readVisiblePx();
            dragStartVisiblePx.current = visiblePx;
            writeVisiblePx(visiblePx);
            el.style.transition = "none";
            // Freeze the FAB where the frozen sheet puts it, so a drag that begins
            // mid-transition doesn't leave the FAB finishing its previous animation.
            applyFabShift(visiblePx, false);
        };

        const handleTouchStart = (e: TouchEvent) => {
            // The container stays mounted after close (its content state is the point), so
            // a touch can still land on it mid slide-out. A closed drawer has no gesture:
            // starting one would drive a snap the open/close effect never asked for.
            if (!isOpenRef.current) {
                return;
            }
            // A second finger joins the gesture in flight rather than restarting it.
            // Resetting here would drop the baseline and, when the first finger lifted,
            // leave the sheet stranded at whatever height it had reached.
            if (gestureTouchId.current !== null) {
                return;
            }
            const touch = e.changedTouches[0];
            gestureTouchId.current = touch.identifier;
            dragStartX.current = touch.clientX;
            dragStartY.current = touch.clientY;
            dragOffset.current = 0;
            lastTouchY.current = touch.clientY;
            lastTouchTime.current = e.timeStamp;

            const dragZone = dragZoneRef.current;
            const onChrome = dragZone !== null && e.target instanceof Node && dragZone.contains(e.target);
            // The drawer's own chrome is always a drag surface, and so is the whole sheet
            // while collapsed: what is on screen then is header, and the body's scroll
            // viewport is a sliver nobody means to scroll. Anywhere else the gesture is
            // content-first and classified on the first move.
            const takesWholeSheet = onChrome || snapPointRef.current === "collapsed";
            gestureMode.current = takesWholeSheet ? "dragging" : "undecided";
            scrollableAncestor.current = takesWholeSheet ? null : findScrollableAncestor(e.target, el);

            if (takesWholeSheet) {
                beginDrag();
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (dragStartY.current === null || dragStartX.current === null || gestureTouchId.current === null) {
                return;
            }
            // Only the finger that started the gesture moves the sheet.
            const touch = findTouch(e.touches, gestureTouchId.current);
            if (!touch) {
                return;
            }

            if (gestureMode.current === "undecided") {
                const deltaX = touch.clientX - dragStartX.current;
                const deltaY = touch.clientY - dragStartY.current;
                if (Math.abs(deltaX) < AXIS_THRESHOLD && Math.abs(deltaY) < AXIS_THRESHOLD) {
                    return;
                }
                // A horizontal gesture, an upward one, or a downward one from anywhere but
                // the top of the content belongs to the content. Only a downward pull from
                // the top moves the sheet.
                const ancestor = scrollableAncestor.current;
                const atTop = !ancestor || ancestor.scrollTop <= 0;
                if (Math.abs(deltaX) > Math.abs(deltaY) || deltaY <= 0 || !atTop) {
                    gestureMode.current = "scrolling";
                } else {
                    gestureMode.current = "dragging";
                    // Rebase on the point the gesture resolved, so the sheet doesn't jump
                    // by the threshold travel that classified it.
                    dragStartY.current = touch.clientY;
                    beginDrag();
                }
            }

            if (gestureMode.current !== "dragging") {
                return;
            }

            if (e.cancelable) {
                e.preventDefault();
            }
            const delta = touch.clientY - dragStartY.current;
            dragOffset.current = delta;

            // The sheet follows the finger. Drag DOWN (positive delta) shows less of it;
            // drag UP (negative delta) shows more. Bottoms out at the collapsed peek, so
            // the chrome can never be dragged off-screen, and tops out at fully open.
            const visiblePx = Math.min(
                maxVisiblePx(),
                Math.max(collapsedHeightRef.current, dragStartVisiblePx.current - delta),
            );
            writeVisiblePx(visiblePx);
            // The FAB rides the pull: home above the collapse boundary, fully off-screen
            // by the peek, so it can never sit over the collapsed header.
            applyFabShift(visiblePx, false);

            lastTouchY.current = touch.clientY;
            lastTouchTime.current = e.timeStamp;
        };

        const handleTouchEnd = (e: TouchEvent) => {
            // The gesture ends when the finger that started it lifts; another finger
            // lifting mid-drag is not a release.
            const id = gestureTouchId.current;
            const touch = id === null ? null : findTouch(e.changedTouches, id);
            if (id !== null && !touch) {
                return;
            }
            gestureTouchId.current = null;
            isDraggingRef.current = false;
            const wasDragging = gestureMode.current === "dragging";
            const startedAt = dragStartY.current;
            dragStartX.current = null;
            gestureMode.current = "undecided";
            scrollableAncestor.current = null;

            // No snap to commit if the sheet never moved — or if the drawer closed under
            // the finger, in which case the slide-out owns the transform now.
            if (startedAt === null || !wasDragging || !isOpenRef.current) {
                dragStartY.current = null;
                dragOffset.current = 0;
                // The sheet never moved, but observer callbacks that arrived while the
                // drag flag was set were dropped. Flush once so the peek is current.
                scheduleMeasurePeek();
                return;
            }

            // Intentionally measured from the last move event, not gesture start —
            // this captures a quick flick at the end of an otherwise slow drag.
            const timeDelta = e.timeStamp - lastTouchTime.current;
            const yDelta = (touch ?? e.changedTouches[0]).clientY - lastTouchY.current;
            // Positive velocity = moving down (collapse), negative = moving up (expand)
            const velocity = timeDelta > 0 ? yDelta / timeDelta : 0;

            // Based on the captured drag-start position so a drag begun mid-transition
            // snaps relative to where the user actually started, not the previous
            // transition's target. Unclamped on purpose: a release well past the floor
            // still reads as "far down" for the nearest-snap decision.
            const nextSnap = nextSnapFor(dragStartVisiblePx.current - dragOffset.current, velocity);

            commitSnap(nextSnap);
            // Same eased travel as the sheet: away if the release collapsed, home otherwise.
            applyFabShift(snapVisiblePx(nextSnap), true);
            snapPointRef.current = nextSnap;
            setSnapPoint(nextSnap);

            dragStartY.current = null;
            dragOffset.current = 0;

            // Hold measurement off for the snap transition — the observer fires on every
            // tick of it — then flush once, picking up whatever was dropped since the
            // drag began.
            isSnapAnimatingRef.current = true;
            window.clearTimeout(snapSettleTimer);
            snapSettleTimer = window.setTimeout(() => {
                isSnapAnimatingRef.current = false;
                measurePeek();
            }, DRAWER_ANIMATION_DURATION);
        };

        el.addEventListener("touchstart", handleTouchStart, { passive: true });
        el.addEventListener("touchmove", handleTouchMove, { passive: false });
        el.addEventListener("touchend", handleTouchEnd, { passive: true });
        el.addEventListener("touchcancel", handleTouchEnd, { passive: true });

        return () => {
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
            el.removeEventListener("touchcancel", handleTouchEnd);
            window.clearTimeout(snapSettleTimer);
            // An orientation flip can tear this down mid-gesture, with no touchend to
            // follow. Left set, these flags would make `measurePeek` bail out for good and
            // the peek would never update again.
            isDraggingRef.current = false;
            isSnapAnimatingRef.current = false;
            gestureTouchId.current = null;
            dragStartX.current = null;
            dragStartY.current = null;
            dragOffset.current = 0;
            gestureMode.current = "undecided";
            scrollableAncestor.current = null;
        };
    }, [isPortrait, applyFabShift, maxVisiblePx, restHeight, snapVisiblePx, measurePeek, scheduleMeasurePeek]);

    return (
        <PanelPickerOverlayContext.Provider value={overlayContextValue}>
            <WindowMobileTargetContext.Provider value={{ target: "drawer", isOpen }}>
                <DrawerPeekTargetContext.Provider value={registerPeekTarget}>
                    <DrawerFabAnchorContext.Provider value={registerFabAnchor}>
                        <DrawerContainer ref={containerRef} isPortrait={isPortrait} data-testid={dataTestId}>
                            <DragZone ref={dragZoneRef} data-testid="drawer-drag-zone">
                                <DragHandleArea>
                                    <DragHandle />
                                </DragHandleArea>
                                {!hideHeader && (
                                    <DrawerHeader className="drawer-header">
                                        <HeaderLeft>
                                            {icon && <HeaderIcon>{icon}</HeaderIcon>}
                                            <Box display="flex" flexDirection="column">
                                                <Typography
                                                    variant={subtitle ? "caption" : "subtitle1"}
                                                    fontWeight={600}
                                                    color={subtitle ? "text.secondary" : undefined}
                                                >
                                                    {title}
                                                </Typography>
                                                {subtitle && (
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        {subtitle}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </HeaderLeft>
                                        <CloseButton onClick={onClose} size="small" title="Close drawer">
                                            <Close />
                                        </CloseButton>
                                    </DrawerHeader>
                                )}
                            </DragZone>
                            <DrawerBody ref={bodyRef}>{children}</DrawerBody>
                            {pickerOverlay !== null && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 10,
                                        bgcolor: "white",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {pickerOverlay}
                                </Box>
                            )}
                        </DrawerContainer>
                    </DrawerFabAnchorContext.Provider>
                </DrawerPeekTargetContext.Provider>
            </WindowMobileTargetContext.Provider>
        </PanelPickerOverlayContext.Provider>
    );
}
